import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { onlyIf, usePermissions } from '../../../platform/auth/permissions';
import { JourneyHelp } from '../../../components/ui/JourneyHelp';
import { PessoaCreateDialog } from './PessoaCreateDialog';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePeople, useCreatePerson } from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { PERSON_STATUS_LABELS, MEMBERSHIP_TYPE_LABELS, labelOf } from '../../../shared/labels';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  CreateForm,
  PagBtn,
  STATUS_VARIANTS,
  Skeleton,
  Td,
  Th,
  createSchema,
  displayName,
} from './pessoa-list.support';

export function PessoaList() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const params: Record<string, unknown> = { page, limit: 20 };
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;

  const { data, isLoading, isError, refetch } = usePeople(params);
  const createPerson = useCreatePerson();

  const people = asList(data);
  const meta = getMeta(data);

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      civilName: '',
      socialName: '',
      email: '',
      phone: '',
      membershipType: 'MEMBER',
      status: 'DRAFT',
    },
  });

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusFilter = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  async function handleCreate(values: CreateForm) {
    if (!can('community.people.create') || createPerson.isPending) return;
    const payload: Record<string, unknown> = {
      civilName: values.civilName.trim(),
      status: values.status ?? 'DRAFT',
    };
    if (values.socialName?.trim()) payload.socialName = values.socialName.trim();
    if (values.email?.trim()) payload.email = values.email.trim();
    if (values.phone?.trim()) payload.phone = values.phone.trim();
    if (values.membershipType) payload.membershipType = values.membershipType;

    try {
      const created = (await createPerson.mutateAsync(payload)) as Record<string, unknown>;
      setShowCreate(false);
      form.reset();
      const id = str(created.id);
      if (id) void navigate(`/pessoas/${id}`);
    } catch {
      /* mutation error surface via isError on mutation if needed */
    }
  }

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="Pessoas" subtitle="Comunidade da casa" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            Não foi possível carregar a lista de pessoas.
          </p>
          <button
            type="button"
            onClick={() => {
              void refetch();
            }}
          >
            Tentar novamente
          </button>
        </Section>
      </div>
    );
  }

  const totalPages = Number(meta.totalPages) || 1;

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="Pessoas"
        subtitle={`${str(meta.total, '0')} pessoas na comunidade`}
        action={onlyIf(can('community.people.create'), {
          label: '+ Nova pessoa',
          onClick: () => {
            setShowCreate(true);
          },
        })}
      />

      <JourneyHelp title="Como cadastrar e encontrar uma pessoa?">
        <p>
          Busque pelo nome ou e-mail e confira os filtros de situação. Abra o nome para consultar o
          cadastro.
        </p>
        {can('community.people.create') && (
          <p>
            Em Nova pessoa, informe o nome civil. Contato e nome de exibição são opcionais.
            Cadastrar não envia convite nem concede acesso digital. Ao salvar, confira o cadastro
            aberto.
          </p>
        )}
        <p>
          Se o envio falhar, seus dados continuam no formulário. Fechá-lo permite retomar nesta
          tela; sair da página descarta o rascunho.
        </p>
      </JourneyHelp>
      <div
        className="journey-filters"
        style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}
      >
        <input
          type="text"
          aria-label="Buscar pessoas por nome ou e-mail"
          placeholder="Buscar por nome ou e-mail..."
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          style={{
            flex: 1,
            maxWidth: 360,
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <select
          aria-label="Filtrar pessoas por situação"
          value={statusFilter}
          onChange={(e) => {
            handleStatusFilter(e.target.value);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
          }}
        >
          <option value="">Todas as situações</option>
          {Object.entries(PERSON_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <Skeleton />}

      {!isLoading && (
        <Section title="Pessoas">
          {people.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: 'var(--faint)',
                fontSize: '0.875rem',
              }}
            >
              {search || statusFilter
                ? 'Nenhuma pessoa encontrada com esses filtros.'
                : can('community.people.create')
                  ? 'Nenhuma pessoa cadastrada. Use Nova pessoa para começar.'
                  : 'Nenhuma pessoa cadastrada. A administração da casa pode incluir os cadastros.'}
            </div>
          ) : (
            <>
              <div className="journey-table">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--line-soft)' }}>
                      <Th>Nome</Th>
                      <Th>Situação</Th>
                      <Th>Contato</Th>
                      <Th>Vínculo</Th>
                      <Th>Acesso digital</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((raw, i) => {
                      const p = asRecord(raw);
                      const st = str(p.status);
                      const member = (p.memberships as Record<string, unknown>[] | undefined)?.[0];
                      return (
                        <tr
                          key={str(p.id, String(i))}
                          onClick={() => {
                            void navigate(`/pessoas/${String(p.id)}`);
                          }}
                          style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--soft)',
                            transition: 'background 0.15s',
                          }}
                        >
                          <Td>
                            <Link
                              to={`/pessoas/${String(p.id)}`}
                              style={{ fontWeight: 500, color: 'var(--green)' }}
                            >
                              {displayName(p)}
                            </Link>
                            {str(p.socialName) && str(p.civilName) !== str(p.socialName) && (
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  color: 'var(--faint)',
                                  marginLeft: '0.5rem',
                                }}
                              >
                                {str(p.civilName)}
                              </span>
                            )}
                          </Td>
                          <Td>
                            <Badge
                              label={labelOf(PERSON_STATUS_LABELS, st)}
                              variant={STATUS_VARIANTS[st] ?? 'default'}
                            />
                          </Td>
                          <Td style={{ fontSize: '0.8rem' }}>
                            {str(p.email, '') && (
                              <div style={{ color: 'var(--muted)' }}>{str(p.email)}</div>
                            )}
                            {str(p.phone, '') && (
                              <div style={{ color: 'var(--muted)' }}>{str(p.phone)}</div>
                            )}
                            {!str(p.email) && !str(p.phone) && '—'}
                          </Td>
                          <Td style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                            {member
                              ? labelOf(MEMBERSHIP_TYPE_LABELS, member.type ?? member.label)
                              : '—'}
                          </Td>
                          <Td>
                            {p.userId ? (
                              <Badge label="Sim" variant="success" />
                            ) : (
                              <span style={{ color: 'var(--line)', fontSize: '0.8rem' }}>—</span>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem',
                  }}
                >
                  <PagBtn
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(page - 1);
                    }}
                    label="<"
                  />
                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
                    <PagBtn
                      key={p}
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                      }}
                      label={String(p)}
                    />
                  ))}
                  <PagBtn
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                    label=">"
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {showCreate && can('community.people.create') && (
        <PessoaCreateDialog
          form={form}
          mutation={createPerson}
          onCreate={() => {
            void form.handleSubmit(handleCreate)();
          }}
          onClose={() => {
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}
